import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { DepartmentService } from 'src/app/services/department.service';
import { EmployeeService } from 'src/app/services/employee.service';
import { PositionService } from 'src/app/services/position.service';
import { UtilsService } from 'src/app/services/utils.service';
import { PermissionService } from 'src/app/services/permission.service';
import { EMPLOYEE_DOCUMENT_TYPES, EmployeeDocumentType } from '../../../document-types';

@Component({
    selector: 'app-general-data-tab',
    templateUrl: './general-data-tab.component.html',
    styleUrls: ['./general-data-tab.component.css', '../employee-tab-shared.css']
})
export class GeneralDataTabComponent implements OnInit {
    @Input() employee: any;
    @Input() mode: 'create' | 'edit' = 'edit';
    @Output() saved = new EventEmitter<any>();

    form!: FormGroup;

    departments: any[] = [];
    positions: any[] = [];

    photoPreviewUrl: string | null = null;
    photoFile: File | null = null;

    documentTypes: EmployeeDocumentType[] = EMPLOYEE_DOCUMENT_TYPES;
    documentsByType: Record<string, any> = {};
    openDocMenuKey: string | null = null;
    dragOverKey: string | null = null;

    private readonly allowedDocumentExtensions = ['pdf', 'jpg', 'jpeg', 'png'];

    constructor(
        private fb: FormBuilder,
        private router: Router,
        private employeeService: EmployeeService,
        private departmentService: DepartmentService,
        private positionService: PositionService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private permissionService: PermissionService
    ) { }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Listado de colaboradores', action);
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            first_name: [this.employee?.first_name || '', Validators.required],
            last_name: [this.employee?.last_name || '', Validators.required],
            mother_last_name: [this.employee?.mother_last_name || ''],
            birth_date: [this.toDateInputValue(this.employee?.birth_date)],
            birth_place: [this.employee?.birth_place || ''],
            fiscal_zip_code: [this.employee?.fiscal_zip_code || ''],
            nss: [this.employee?.nss || ''],
            curp: [this.employee?.curp || ''],
            rfc: [this.employee?.rfc || ''],
            rfc_previously_registered_sat: [!!this.employee?.rfc_previously_registered_sat],
            gender: [this.employee?.gender || ''],
            department_id: [this.employee?.department_id || ''],
            position_id: [this.employee?.position_id || ''],
            hire_date: [this.toDateInputValue(this.employee?.hire_date), Validators.required],
            termination_date: [{ value: this.toDateInputValue(this.employee?.termination_date), disabled: this.employee?.status !== 'terminated' }],
            work_mode: [this.employee?.work_mode || '']
        });

        this.photoPreviewUrl = this.employeeService.getPhotoUrl(this.employee?.photo);
        this.rebuildDocumentsByType();

        this.loadDepartments();
        if (this.employee?.department_id) {
            this.loadPositions(this.employee.department_id);
        }
    }

    get f() {
        return this.form.controls;
    }

    get isCreateMode(): boolean {
        return this.mode === 'create';
    }

    toDateInputValue(value: any): string {
        if (!value) return '';
        return String(value).substring(0, 10);
    }

    loadDepartments(): void {
        this.departmentService.list().subscribe({
            next: (resp: any) => {
                this.departments = Array.isArray(resp) ? resp : [];
            },
            error: () => {
                this.departments = [];
            }
        });
    }

    loadPositions(departmentId: any): void {
        this.positionService.list(departmentId).subscribe({
            next: (resp: any) => {
                this.positions = Array.isArray(resp) ? resp : [];
            },
            error: () => {
                this.positions = [];
            }
        });
    }

    onDepartmentChange(): void {
        const departmentId = this.form.get('department_id')?.value;
        this.form.get('position_id')?.setValue('');
        this.positions = [];
        if (departmentId) {
            this.loadPositions(departmentId);
        }
    }

    onFileChange(event: any): void {
        const file = event?.target?.files?.[0];
        if (!file) return;

        this.photoFile = file;
        if (this.photoPreviewUrl && this.photoPreviewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(this.photoPreviewUrl);
        }
        this.photoPreviewUrl = URL.createObjectURL(file);
        this.uploadPhoto();
    }

    uploadPhoto(): void {
        if (!this.photoFile || !this.employee?.id) return;

        const formData = new FormData();
        formData.append('file', this.photoFile);

        this.spinner.show();
        this.employeeService.uploadPhoto(this.employee.id, formData).subscribe({
            next: (resp: any) => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Operación exitosa', text: 'La fotografía se actualizó correctamente', icon: 'success' });
                this.saved.emit({ photo: resp?.photo });
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo cargar la fotografía', icon: 'error' });
            }
        });
    }

    rebuildDocumentsByType(): void {
        this.documentsByType = {};
        const documents = Array.isArray(this.employee?.documents) ? this.employee.documents : [];
        for (const doc of documents) {
            this.documentsByType[doc.document_type] = doc;
        }
    }

    getDocumentUrl(doc: any): string | null {
        return this.employeeService.getDocumentUrl(doc?.file_path);
    }

    onDocumentFileChange(event: any, docType: EmployeeDocumentType): void {
        const file = event?.target?.files?.[0];
        event.target.value = '';
        this.handleDocumentFile(file, docType);
    }

    onDocumentDragOver(event: DragEvent, docType: EmployeeDocumentType): void {
        event.preventDefault();
        event.stopPropagation();
        this.dragOverKey = docType.key;
    }

    onDocumentDragLeave(event: DragEvent, docType: EmployeeDocumentType): void {
        event.preventDefault();
        event.stopPropagation();
        if (this.dragOverKey === docType.key) {
            this.dragOverKey = null;
        }
    }

    onDocumentDrop(event: DragEvent, docType: EmployeeDocumentType): void {
        event.preventDefault();
        event.stopPropagation();
        this.dragOverKey = null;

        const file = event.dataTransfer?.files?.[0];
        this.handleDocumentFile(file, docType);
    }

    private handleDocumentFile(file: File | undefined | null, docType: EmployeeDocumentType): void {
        if (!file) return;

        if (!this.employee?.id) {
            this.utils.alertSwal({ title: 'Advertencia', text: 'Guarda primero los datos generales para poder subir documentos.', icon: 'warning' });
            return;
        }

        const extension = file.name.split('.').pop()?.toLowerCase() || '';
        if (!this.allowedDocumentExtensions.includes(extension)) {
            this.utils.alertSwal({ title: 'Advertencia', text: 'Formato no permitido. Usa PDF, JPG o PNG.', icon: 'warning' });
            return;
        }

        const formData = new FormData();
        formData.append('type', docType.key);
        formData.append('file', file);

        this.spinner.show();
        this.employeeService.uploadDocument(this.employee.id, formData).subscribe({
            next: (resp: any) => {
                this.spinner.hide();
                this.documentsByType[docType.key] = resp?.document;
                this.utils.alertSwal({ title: 'Operación exitosa', text: 'El documento se cargó correctamente', icon: 'success' });
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo cargar el documento', icon: 'error' });
            }
        });
    }

    toggleDocMenu(docType: string): void {
        this.openDocMenuKey = this.openDocMenuKey === docType ? null : docType;
    }

    @HostListener('document:click')
    closeDocMenu(): void {
        this.openDocMenuKey = null;
    }

    viewDocument(docType: EmployeeDocumentType): void {
        if (!this.employee?.id) return;
        this.router.navigate(['/employee', this.employee.id, 'document', docType.key]);
    }

    deleteDocumentFile(docType: EmployeeDocumentType): void {
        if (!this.employee?.id) return;

        this.utils.alertSwalConfir({
            title: '¿Eliminar archivo?',
            text: `Se eliminará "${this.documentsByType[docType.key]?.original_name || docType.label}".`,
            icon: 'warning',
            btn_confirm: 'Eliminar'
        }).then((result: any) => {
            if (!result?.isConfirmed) return;

            this.spinner.show();
            this.employeeService.deleteDocument(this.employee.id, docType.key).subscribe({
                next: () => {
                    this.spinner.hide();
                    delete this.documentsByType[docType.key];
                    this.utils.alertSwal({ title: 'Operación exitosa', text: 'El documento se eliminó correctamente', icon: 'success' });
                },
                error: () => {
                    this.spinner.hide();
                    this.utils.alertSwal({ title: 'Error', text: 'No se pudo eliminar el documento', icon: 'error' });
                }
            });
        });
    }

    save(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.utils.alertSwal({ title: 'Advertencia', text: 'Verifique que la información se encuentre completa.', icon: 'warning' });
            return;
        }

        const value = this.form.getRawValue();
        const payload: any = {
            first_name: value.first_name,
            last_name: value.last_name,
            mother_last_name: value.mother_last_name,
            birth_date: value.birth_date || null,
            birth_place: value.birth_place,
            fiscal_zip_code: value.fiscal_zip_code,
            nss: value.nss,
            curp: value.curp,
            rfc: value.rfc,
            rfc_previously_registered_sat: value.rfc_previously_registered_sat,
            gender: value.gender,
            department_id: value.department_id || null,
            position_id: value.position_id || null,
            hire_date: value.hire_date || null,
            work_mode: value.work_mode
        };

        if (this.isCreateMode) {
            this.createEmployee(payload);
        } else {
            this.updateEmployee(payload, value);
        }
    }

    private createEmployee(payload: any): void {
        this.spinner.show();
        this.employeeService.create(payload).subscribe({
            next: (resp: any) => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Operación exitosa', text: 'El colaborador se registró correctamente', icon: 'success' });

                const newId = resp?.id;
                if (newId) {
                    // Reemplaza la URL para dejar al usuario en modo edición real,
                    // con el historial de navegación limpio (sin la pantalla "new").
                    this.router.navigate(['/employee', newId], { replaceUrl: true });
                }
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo registrar el colaborador', icon: 'error' });
            }
        });
    }

    private updateEmployee(payload: any, value: any): void {
        this.spinner.show();
        this.employeeService.update(this.employee.id, payload).subscribe({
            next: (resp: any) => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Operación exitosa', text: 'Los datos generales se guardaron correctamente', icon: 'success' });

                const selectedDepartment = this.departments.find((d: any) => String(d.id) === String(value.department_id)) || null;
                const selectedPosition = this.positions.find((p: any) => String(p.id) === String(value.position_id)) || null;

                this.saved.emit({
                    ...resp,
                    department: selectedDepartment,
                    position: selectedPosition
                });
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo guardar la información', icon: 'error' });
            }
        });
    }
}
